import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  FormControlLabel,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Collapse,
  IconButton,
  TextareaAutosize,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  ExpandLess,
  ExpandMore,
  Upload,
  Download
} from '@mui/icons-material';
import CarrierCard from '../CarrierCard/CarrierCard';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';

const SearchDashboard = ({
  onSearch,
  onBatchSearch,
  clearBatchResults,
  loading,
  error,
  carrierData,
  showBasicInfo,
  onToggleView
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('auto');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [batchInput, setBatchInput] = useState('');
  const [filters, setFilters] = useState({
    skipBrokers: false,
    skipInactive: false,
    showInactiveOnly: false,
    activeDuration: 0,
    resultLimit: 10,
    state: '',
    states: []
  });

  const handleSearch = () => {
    if (searchTerm.trim() || searchType === 'range') {
      onSearch(searchTerm.trim(), searchType, filters);
    }
  };

  const handleBatchSearch = () => {
    if (batchInput.trim()) {
      onBatchSearch(batchInput.trim(), filters);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (activeTab === 0) {
        handleSearch();
      } else {
        handleBatchSearch();
      }
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearSearch = () => {
    setSearchTerm('');
    setBatchInput('');
    setFilters({
      skipBrokers: false,
      skipInactive: false,
      showInactiveOnly: false,
      activeDuration: 0,
      resultLimit: 10,
      state: '',
      states: []
    });
  };

  const quickSearch = (type, value) => {
    setSearchType(type);
    setSearchTerm(value);
    setTimeout(() => handleSearch(), 100);
  };

  const detectSearchType = (input) => {
    const trimmed = input.trim();

    if (/^MC-?\d{6,8}$/i.test(trimmed) || /^\d{6,8}$/.test(trimmed)) {
      return 'mc';
    }
    if (/^\d{7,8}$/.test(trimmed)) {
      return 'dot';
    }
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return 'email';
    }
    if (/^\d+-\d+$/.test(trimmed)) {
      return 'range';
    }
    if (/^[a-zA-Z\s.,&]+$/.test(trimmed) && trimmed.length > 2) {
      return 'name';
    }
    return 'auto';
  };

  const searchTypes = [
    { id: 'auto', label: 'Auto Detect', placeholder: 'Enter MC, DOT, email, or name...', example: 'MC-123456' },
    { id: 'mc', label: 'MC Number', placeholder: 'Enter MC number...', example: '123456' },
    { id: 'dot', label: 'DOT Number', placeholder: 'Enter DOT number...', example: '2874689' },
    { id: 'email', label: 'Email', placeholder: 'Enter email address...', example: 'carrier@company.com' },
    { id: 'name', label: 'Company Name', placeholder: 'Enter company name...', example: 'ABC Trucking' },
    { id: 'range', label: 'Number Range', placeholder: 'e.g., 100000-100050', example: '100000-100050' }
  ];

  const quickExamples = [
    { type: 'mc', label: 'MC Search', value: '123456' },
    { type: 'dot', label: 'DOT Search', value: '2874689' },
    { type: 'range', label: 'Range Search', value: '100000-100010' },
    { type: 'email', label: 'Email Search', value: 'carrier@example.com' },
    { type: 'name', label: 'Name Search', value: 'ABC Trucking' }
  ];

  const batchExamples = `123456
2874689
MC-654321
carrier@example.com
ABC Trucking
100000-100005`;
// inside SearchDashboard component
 const exportCSV = (payload) => {
   // Normalize to array of carrier rows
   const rows = payload?.searchType === 'batch'
    ? (payload.batchResults || [])
    : (payload ? [payload] : []);

  if (!rows.length) return;

   // Choose columns
   const headers = [
    'Legal Name','DBA','DOT','MC','State(Physical)','State(Mailing)',
    'Phone','Email','Status','Carrier Operation','Power Units','Drivers','Active Duration',
    'Physical Address','Mailing Address'
  ];

   const esc = (v) => {
     const s = (v === undefined || v === null) ? '' : String(v);
return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const lines = [headers.join(',')];
   rows.forEach(c => {
     lines.push([
       c.legalName, c.dbaName, c.dotNumber, c.mcNumber,
       c.phyState || '', c.mailState || '',
       c.phone, c.email, c.status, c.carrierOperation,
       c.powerUnits, c.totalDrivers,
       c.activeDuration,
       c.physicalAddress, c.mailingAddress
     ].map(esc).join(','));
   });

   const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
   const url = URL.createObjectURL(blob);
   const a = document.createElement('a');
   const ts = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
   a.href = url;
   a.download = `carriers-${ts}.csv`;
   document.body.appendChild(a);   a.click();
   document.body.removeChild(a);
   URL.revokeObjectURL(url);
 };
const exportPDF = async (payload, selectedStates = []) => {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  // Normalize rows
  const rows = payload?.searchType === 'batch'
    ? (payload.batchResults || [])
    : (payload ? [payload] : []);

  if (!rows.length) return;

  // Group by state (prefer physical, then mailing, else 'Unknown')
  const groupKey = (c) => (c.phyState || c.mailState || 'Unknown').toUpperCase();
  const grouped = {};
  rows.forEach(c => {
    const key = groupKey(c);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  });

  // If multi-select applied, order by selectedStates first; else alpha
  const order = (Array.isArray(selectedStates) && selectedStates.length)
    ? [...selectedStates.map(s => s.toUpperCase()), ...Object.keys(grouped).filter(k => !selectedStates.map(s=>s.toUpperCase()).includes(k)).sort()]
    : Object.keys(grouped).sort();

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  const columns = [
    { header: 'Legal Name', dataKey: 'legalName' },
    { header: 'DBA', dataKey: 'dbaName' },
    { header: 'DOT', dataKey: 'dotNumber' },
    { header: 'MC', dataKey: 'mcNumber' },
    { header: 'Phone', dataKey: 'phone' },
    { header: 'Email', dataKey: 'email' },
    { header: 'Op', dataKey: 'carrierOperation' },
    { header: 'Units', dataKey: 'powerUnits' },
    { header: 'Drivers', dataKey: 'totalDrivers' },
    { header: 'Status', dataKey: 'status' },
  ];

  let firstPage = true;

  for (const st of order) {
    const data = grouped[st];
    if (!data || !data.length) continue;

    if (!firstPage) doc.addPage();
    firstPage = false;

    // Title
    doc.setFontSize(16);
    doc.text(`Carriers — ${st}`, 40, 40);

    // Subline (date & count)
    doc.setFontSize(10);
    doc.text(`Exported: ${new Date().toLocaleString()}  |  Records: ${data.length}`, 40, 58);

    // Table
    autoTable(doc, {
      startY: 75,
      head: [columns.map(c => c.header)],
      body: data.map(c => columns.map(col => c[col.dataKey] ?? '')),
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fontStyle: 'bold' },
      margin: { left: 40, right: 40 },
      tableWidth: 'auto',
      didDrawPage: (d) => {
        // Footer page number
        const str = `Page ${doc.getCurrentPageInfo().pageNumber}`;
        doc.setFontSize(9);
        doc.text(str, doc.internal.pageSize.getWidth() - 60, doc.internal.pageSize.getHeight() - 20);
      }
    });

    // Optional: Addresses table (second table on same page)
     autoTable(doc, {
       startY: doc.lastAutoTable.finalY + 12,
      head: [['Physical Address', 'Mailing Address']],
      body: data.map(c => [c.physicalAddress || '', c.mailingAddress || '']),
      styles: { fontSize: 8, cellPadding: 4 },
      margin: { left: 40, right: 40 },
     });
  }

  const ts = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
  doc.save(`carriers-statewise-${ts}.pdf`);
};

  const durationMarks = [
    { value: 0, label: 'Any' },
    { value: 6, label: '6M' },
    { value: 12, label: '1Y' },
    { value: 24, label: '2Y' },
    { value: 60, label: '5Y' }
  ];

  const hasActiveFilters = filters.skipBrokers || filters.skipInactive ||
    filters.showInactiveOnly || filters.activeDuration > 0 ||
    filters.resultLimit !== 10;

  const isBatchActive = carrierData && carrierData.searchType === 'batch';

  return (
    
    <Box sx={{px: { xs: 2, sm: 3, md: 4 } }}>
      

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Single Search" />
            <Tab label="Batch Search" />
          </Tabs>

          <Box sx={{ p: 2 }}>
            {activeTab === 0 && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                    Quick Search:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {quickExamples.map((item, index) => (
                      <Chip
                        key={index}
                        label={item.label}
                        size="small"
                        variant="outlined"
                        onClick={() => quickSearch(item.type, item.value)}
                        clickable
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                </Box>

                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Search Type</InputLabel>
                      <Select
                        value={searchType}
                        onChange={(e) => setSearchType(e.target.value)}
                        label="Search Type"
                      >
                        {searchTypes.map(type => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder={searchTypes.find(t => t.id === searchType)?.placeholder}
                      value={searchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchTerm(value);
                        if (searchType === 'auto' && value.trim()) {
                          const detectedType = detectSearchType(value);
                          if (detectedType !== 'auto') {
                            
                          }
                        }
                      }}
                      onKeyPress={handleKeyPress}
                      disabled={loading}
                      helperText={
                        searchType === 'auto' && searchTerm.trim()
                          ? `Detected: ${detectSearchType(searchTerm)}`
                          : searchTypes.find(t => t.id === searchType)?.example
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={loading || (!searchTerm.trim() && searchType !== 'range')}
                        startIcon={<SearchIcon />}
                        size="small"
                        sx={{ flex: 1 }}
                      >
                        {loading ? '...' : 'Search'}
                      </Button>

                      <IconButton
                        size="small"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        color={showAdvanced ? "primary" : "default"}
                      >
                        <FilterIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}

            {activeTab === 1 && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                    Batch Search - Enter multiple search terms (one per line):
                  </Typography>

                  <TextareaAutosize
                    minRows={6}
                    maxRows={12}
                    placeholder={`Enter multiple search terms, one per line:\n${batchExamples}`}
                    value={batchInput}
                    onChange={(e) => setBatchInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      background: 'rgba(255,255,255,0.05)',
                      color: 'inherit'
                    }}
                  />

                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      startIcon={<Download />}
                      onClick={() => setBatchInput(batchExamples)}
                      variant="outlined"
                    >
                      Load Example
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setBatchInput('')}
                      variant="outlined"
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleBatchSearch}
                    disabled={loading || !batchInput.trim()}
                    startIcon={<SearchIcon />}
                    sx={{ flex: 1 }}
                  >
                    {loading ? 'Processing Batch...' : 'Start Live Batch Search'}
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    color="error"
                    onClick={() => {
                      if (clearBatchResults) clearBatchResults();
                      setBatchInput('');
                    }}
                    disabled={!isBatchActive}
                    sx={{ ml: 0 }}
                  >
                    Clear Results
                  </Button>

                  <IconButton
                    size="small"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    color={showAdvanced ? "primary" : "default"}
                  >
                    <FilterIcon />
                  </IconButton>
                </Box>
              </>
            )}

            <Collapse in={showAdvanced}>
              <Box sx={{ mt: 2, p: 2, background: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>Carrier Type</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={filters.skipBrokers}
                            onChange={(e) => handleFilterChange('skipBrokers', e.target.checked)}
                          />
                        }
                        label="Exclude Brokers"
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={filters.skipInactive}
                            onChange={(e) => handleFilterChange('skipInactive', e.target.checked)}
                          />
                        }
                        label="Active Only"
                      />
                    </Box>
                  </Grid>
<Grid item xs={12} md={6}>
  <Typography variant="subtitle2" gutterBottom>States</Typography>
  <FormControl fullWidth size="small">
    <InputLabel>States</InputLabel>
    <Select
      multiple
      value={filters.states}
      label="States"
      onChange={(e) => handleFilterChange('states', e.target.value)}
      renderValue={(selected) => selected.join(', ')}
    >
      {[
        'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
      ].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
    </Select>
  </FormControl>
</Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      Min Active: {filters.activeDuration === 0 ? 'Any' : `${filters.activeDuration}M`}
                    </Typography>
                    <Slider
                      size="small"
                      value={filters.activeDuration}
                      onChange={(e, newValue) => handleFilterChange('activeDuration', newValue)}
                      min={0}
                      max={60}
                      step={1}
                      marks={durationMarks}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => value === 0 ? 'Any' : `${value}M`}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Max Results</InputLabel>
                      <Select
                        value={filters.resultLimit}
                        onChange={(e) => handleFilterChange('resultLimit', e.target.value)}
                        label="Max Results"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>

            {hasActiveFilters && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {filters.skipBrokers && (
                    <Chip
                      label="No Brokers"
                      size="small"
                      color="primary"
                      onDelete={() => handleFilterChange('skipBrokers', false)}
                    />
                  )}
                  {filters.skipInactive && (
                    <Chip
                      label="Active Only"
                      size="small"
                      color="success"
                      onDelete={() => handleFilterChange('skipInactive', false)}
                    />
                  )}
                  {filters.activeDuration > 0 && (
                    <Chip
                      label={`Min ${filters.activeDuration}M`}
                      size="small"
                      color="warning"
                      onDelete={() => handleFilterChange('activeDuration', 0)}
                    />
                  )}
                  {filters.resultLimit !== 10 && (
                    <Chip
                      label={`Max ${filters.resultLimit}`}
                      size="small"
                      onDelete={() => handleFilterChange('resultLimit', 10)}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity={error.includes('found') ? 'warning' : 'error'} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
 <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, gap: 1 }}>
  <Button
    size="small"
    variant="outlined"
    startIcon={<Download />}
    disabled={!carrierData}
    onClick={() => exportCSV(carrierData)}
  >
    Export CSV
  </Button>
  <Button
   size="small"
   variant="contained"
   startIcon={<Download />} 
   disabled={!carrierData}
   onClick={() => exportPDF(carrierData, filters.states)}
>
   Export PDF
 </Button>
 </Box>
      <Box sx={{ minHeight: '400px' }}>
        {carrierData && carrierData.searchType === 'batch' ? (
          <CarrierCard
            data={carrierData}
            showBasicInfo={showBasicInfo}
            onToggleView={onToggleView}
          />
        ) : loading ? (
          <SkeletonLoader />
        ) : carrierData ? (
          <CarrierCard
            data={carrierData}
            showBasicInfo={showBasicInfo}
            onToggleView={onToggleView}
          />
        ) : (
          <Card sx={{
            textAlign: 'center',
            py: 6,
            background: 'transparent',
            border: '2px dashed rgba(255,255,255,0.1)'
          }}>
            <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
            <Typography variant="body1" color="text.secondary">
              {activeTab === 0 ? 'Enter search criteria to find carriers' : 'Enter multiple search terms for batch processing'}
            </Typography>
          </Card>
        )}
      </Box>
    </Box>


  );
};

export default SearchDashboard;